import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import oiExportService from "@/services/oiExportService";
import type {
  OIExportEstimateResponse,
  OIExportExpiryType,
  OIExportFormat,
  OIExportInterval,
  OIExportJobRead,
  OIExportJobStatus,
  OIExportOptionType,
  OIExportResolutionSummaryResponse,
  OIExportStrikeMode,
  OIExportDataset,
} from "@/types";
import { extractErrorMessage } from "@/utils/errors";

// Same hardcoded pair as OIBackfillPage's SYMBOL_OPTIONS, for the same
// reason: only NIFTY/BANKNIFTY have any seeded OI data, and there is no
// generic "list instruments" endpoint yet to fetch this from.
const SYMBOL_OPTIONS = ["NIFTY", "BANKNIFTY"] as const;

const EXPIRY_TYPE_OPTIONS: Array<{ label: string; value: OIExportExpiryType }> = [
  { label: "Current Weekly", value: "CURRENT_WEEKLY" },
  { label: "Next Weekly", value: "NEXT_WEEKLY" },
  { label: "Current Monthly", value: "CURRENT_MONTHLY" },
  { label: "All", value: "ALL" },
];

const OPTION_TYPE_OPTIONS: Array<{ label: string; value: OIExportOptionType }> = [
  { label: "CE", value: "CE" },
  { label: "PE", value: "PE" },
  { label: "Both", value: "BOTH" },
];

// "1 Minute" and "5 Minute" are both raw passthroughs of the stored rows
// (verified: real backfilled data is a mix of 1-minute and 5-minute
// cadence depending on how close a contract was to its own expiry when it
// was fetched — see `app.models.oi_export.OIExportInterval`'s docstring).
// "1 Minute" does NOT guarantee every exported row is actually 1-minute
// resolution; it exports the real stored records with their real
// `captured_at` timestamps and adds an `actual_interval` column so the
// true cadence of each row is visible. "15 Minute"/"Hourly"/"Daily" remain
// server-side rollups of the 5-minute data, unchanged.
const INTERVAL_OPTIONS: Array<{ label: string; value: OIExportInterval }> = [
  { label: "1 Minute", value: "1minute" },
  { label: "5 Minute", value: "5minute" },
  { label: "15 Minute", value: "15minute" },
  { label: "Hourly", value: "hourly" },
  { label: "Daily", value: "daily" },
];

// Shown under the Interval selector only when "1 Minute" is selected —
// requirement #2: never let the UI imply this guarantees 1-minute
// resolution for every row.
const ONE_MINUTE_HELPER_TEXT =
  'Exports the actual stored records at their real timestamps — some sessions were only captured at 5-minute cadence, so not every row will be 1-minute resolution. Each row includes an "Actual Interval" column showing its real cadence.';

const EXPORT_FORMAT_OPTIONS: Array<{ label: string; value: OIExportFormat }> = [
  { label: "CSV", value: "CSV" },
  { label: "Excel (.xlsx)", value: "XLSX" },
  { label: "Compressed ZIP", value: "ZIP" },
];

const JOB_STATUS_FILTER_OPTIONS: Array<{ label: string; value: OIExportJobStatus | "" }> = [
  { label: "All statuses", value: "" },
  { label: "Queued", value: "QUEUED" },
  { label: "Running", value: "RUNNING" },
  { label: "Completed", value: "COMPLETED" },
  { label: "Failed", value: "FAILED" },
];

type ChipColor = "default" | "info" | "warning" | "success" | "error";

const JOB_STATUS_COLOR: Record<OIExportJobStatus, ChipColor> = {
  QUEUED: "default",
  RUNNING: "info",
  COMPLETED: "success",
  FAILED: "error",
};

const NON_TERMINAL_EXPORT_STATUSES: OIExportJobStatus[] = ["QUEUED", "RUNNING"];

// Debounce delay between the filter form settling and the estimate call
// firing — short enough to feel live, long enough that typing a strike
// range doesn't fire one request per keystroke.
const ESTIMATE_DEBOUNCE_MS = 500;

function ExportStatusChip({ status }: { status: OIExportJobStatus }) {
  return <Chip label={status} size="small" color={JOB_STATUS_COLOR[status]} />;
}

function formatDateTime(value?: string | null): string {
  return value ? new Date(value).toLocaleString() : "—";
}

function formatRecordCount(job: OIExportJobRead): string {
  if (job.record_count != null) return job.record_count.toLocaleString();
  if (job.estimated_record_count != null) {
    return `~${job.estimated_record_count.toLocaleString()}`;
  }
  return "—";
}

function isoDateDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

export function OIExportSection() {
  // ---- Filters ----
  const [dataset, setDataset] = useState<OIExportDataset>("OI_CHAIN");
  const isMinuteDataset = dataset === "OPTION_MINUTE_1MIN";
  const [symbolCode, setSymbolCode] = useState<string>("NIFTY");
  const [fromDate, setFromDate] = useState(isoDateDaysAgo(7));
  const [toDate, setToDate] = useState(isoToday());
  const [expiryType, setExpiryType] = useState<OIExportExpiryType>("ALL");
  const [optionType, setOptionType] = useState<OIExportOptionType>("BOTH");
  const [strikeMode, setStrikeMode] = useState<OIExportStrikeMode>("ATM_BAND");
  const [atmBand, setAtmBand] = useState(10);
  const [strikeMin, setStrikeMin] = useState<string>("");
  const [strikeMax, setStrikeMax] = useState<string>("");
  const [interval, setIntervalValue] = useState<OIExportInterval>("5minute");
  const [exportFormat, setExportFormat] = useState<OIExportFormat>("CSV");

  const filtersValid =
    Boolean(symbolCode && fromDate && toDate) &&
    (strikeMode === "ATM_BAND" ? atmBand >= 1 : strikeMode === "ALL_STORED" ? true : strikeMin !== "" && strikeMax !== "");

  const changeDataset = (next: OIExportDataset) => {
    setDataset(next);
    if (next === "OPTION_MINUTE_1MIN") {
      setIntervalValue("1minute");
      if (strikeMode === "ATM_BAND") setStrikeMode("ALL_STORED");
    } else {
      setIntervalValue("5minute");
      if (strikeMode === "ALL_STORED") setStrikeMode("ATM_BAND");
    }
  };

  // ---- Estimate (requirement #4: show estimated record count before export) ----
  const [estimate, setEstimate] = useState<OIExportEstimateResponse | null>(null);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [estimateError, setEstimateError] = useState<string | null>(null);

  useEffect(() => {
    if (!filtersValid) {
      setEstimate(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      setEstimateLoading(true);
      setEstimateError(null);
      oiExportService
        .estimate({
          symbol_code: symbolCode,
          from_date: fromDate,
          to_date: toDate,
          expiry_type: expiryType,
          option_type: optionType,
          strike_mode: strikeMode,
          atm_band: strikeMode === "ATM_BAND" ? atmBand : null,
          strike_min: strikeMode === "CUSTOM_RANGE" ? Number(strikeMin) : null,
          strike_max: strikeMode === "CUSTOM_RANGE" ? Number(strikeMax) : null,
          interval,
          dataset,
        })
        .then((result) => {
          if (!cancelled) setEstimate(result);
        })
        .catch((err) => {
          if (!cancelled) {
            setEstimate(null);
            setEstimateError(extractErrorMessage(err));
          }
        })
        .finally(() => {
          if (!cancelled) setEstimateLoading(false);
        });
    }, ESTIMATE_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbolCode, fromDate, toDate, expiryType, optionType, strikeMode, atmBand, strikeMin, strikeMax, interval, filtersValid, dataset]);

  // ---- Resolution summary (requirement #6/#7: data-quality summary +
  // mixed-resolution warning before export, independent of `interval` —
  // the actual stored cadence for a given filter set doesn't change based
  // on which export interval is selected, so this is fetched once per
  // filter change, not re-fetched when only `interval` changes). ----
  const [resolutionSummary, setResolutionSummary] = useState<OIExportResolutionSummaryResponse | null>(null);
  const [resolutionSummaryLoading, setResolutionSummaryLoading] = useState(false);
  const [resolutionSummaryError, setResolutionSummaryError] = useState<string | null>(null);

  useEffect(() => {
    if (!filtersValid) {
      setResolutionSummary(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      setResolutionSummaryLoading(true);
      setResolutionSummaryError(null);
      oiExportService
        .summarizeResolution({
          symbol_code: symbolCode,
          from_date: fromDate,
          to_date: toDate,
          expiry_type: expiryType,
          option_type: optionType,
          strike_mode: strikeMode,
          atm_band: strikeMode === "ATM_BAND" ? atmBand : null,
          strike_min: strikeMode === "CUSTOM_RANGE" ? Number(strikeMin) : null,
          strike_max: strikeMode === "CUSTOM_RANGE" ? Number(strikeMax) : null,
          dataset,
        })
        .then((result) => {
          if (!cancelled) setResolutionSummary(result);
        })
        .catch((err) => {
          if (!cancelled) {
            setResolutionSummary(null);
            setResolutionSummaryError(extractErrorMessage(err));
          }
        })
        .finally(() => {
          if (!cancelled) setResolutionSummaryLoading(false);
        });
    }, ESTIMATE_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbolCode, fromDate, toDate, expiryType, optionType, strikeMode, atmBand, strikeMin, strikeMax, filtersValid, dataset]);

  // ---- Create export job ----
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createNotice, setCreateNotice] = useState<string | null>(null);

  const handleCreateJob = async () => {
    setCreating(true);
    setCreateError(null);
    setCreateNotice(null);
    try {
      const job = await oiExportService.createJob({
        symbol_code: symbolCode,
        from_date: fromDate,
        to_date: toDate,
        expiry_type: expiryType,
        option_type: optionType,
        strike_mode: strikeMode,
        atm_band: strikeMode === "ATM_BAND" ? atmBand : null,
        strike_min: strikeMode === "CUSTOM_RANGE" ? Number(strikeMin) : null,
        strike_max: strikeMode === "CUSTOM_RANGE" ? Number(strikeMax) : null,
        interval,
        export_format: exportFormat,
        dataset,
      });
      setCreateNotice(
        NON_TERMINAL_EXPORT_STATUSES.includes(job.status)
          ? `Export ${job.id} queued and running in the background — it will appear as Completed below once ready.`
          : job.status === "COMPLETED"
          ? `Export ${job.id} completed — download it from the table below.`
          : `Export ${job.id} failed: ${job.error_message ?? "unknown error"}`
      );
      await loadJobs();
    } catch (err) {
      setCreateError(extractErrorMessage(err));
    } finally {
      setCreating(false);
    }
  };

  // ---- Export Jobs table ----
  const [jobs, setJobs] = useState<OIExportJobRead[]>([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsError, setJobsError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<OIExportJobStatus | "">("");

  const loadJobs = useCallback(
    async (opts: { silent?: boolean } = {}) => {
      if (!opts.silent) setJobsLoading(true);
      try {
        const result = await oiExportService.listJobs({ status: statusFilter || undefined, limit: 100 });
        setJobs(result);
        setJobsError(null);
      } catch (err) {
        setJobsError(extractErrorMessage(err));
      } finally {
        if (!opts.silent) setJobsLoading(false);
      }
    },
    [statusFilter]
  );

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  // Silently refresh every 8s while at least one listed job is still
  // QUEUED/RUNNING — same convention as OIBackfillPage's own job list.
  useEffect(() => {
    const hasActiveJob = jobs.some((j) => NON_TERMINAL_EXPORT_STATUSES.includes(j.status));
    if (!hasActiveJob) return;
    const timer = setInterval(() => loadJobs({ silent: true }), 8000);
    return () => clearInterval(timer);
  }, [jobs, loadJobs]);

  // ---- Download ----
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownload = async (job: OIExportJobRead) => {
    setDownloadingId(job.id);
    setDownloadError(null);
    try {
      await oiExportService.downloadFile(job);
    } catch (err) {
      setDownloadError(extractErrorMessage(err));
    } finally {
      setDownloadingId(null);
    }
  };

  // ---- Delete ----
  const [deleteTarget, setDeleteTarget] = useState<OIExportJobRead | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const deletingRef = useRef(false);

  const handleDelete = async () => {
    if (!deleteTarget || deletingRef.current) return;
    deletingRef.current = true;
    setDeleting(true);
    setDeleteError(null);
    try {
      await oiExportService.deleteJob(deleteTarget.id);
      setDeleteTarget(null);
      await loadJobs();
    } catch (err) {
      setDeleteError(extractErrorMessage(err));
    } finally {
      setDeleting(false);
      deletingRef.current = false;
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Export Historical OI Data
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Download historical Open Interest data already stored in the database as CSV, Excel, or a
        compressed ZIP. Small exports complete immediately; large exports run in the background and
        appear below once ready. This is independent of the backfill jobs above — it only reads data
        that has already been collected.
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Export Filters
          </Typography>

          {createError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCreateError(null)}>
              {createError}
            </Alert>
          )}
          {createNotice && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCreateNotice(null)}>
              {createNotice}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                label="Dataset"
                value={dataset}
                onChange={(e) => changeDataset(e.target.value as OIExportDataset)}
                helperText={isMinuteDataset ? "1-minute option prices stored for backtests (Option prices job type above)" : undefined}
                fullWidth
              >
                <MenuItem value="OI_CHAIN">OI option chain</MenuItem>
                <MenuItem value="OPTION_MINUTE_1MIN">Option prices 1-minute (backtest store)</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                select
                label="Symbol"
                value={symbolCode}
                onChange={(e) => setSymbolCode(e.target.value)}
                fullWidth
              >
                {SYMBOL_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                label="From date"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                label="To date"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                select
                label="Expiry Type"
                value={expiryType}
                onChange={(e) => setExpiryType(e.target.value as OIExportExpiryType)}
                fullWidth
              >
                {EXPIRY_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                select
                label="Option Type"
                value={optionType}
                onChange={(e) => setOptionType(e.target.value as OIExportOptionType)}
                fullWidth
              >
                {OPTION_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                select
                label="Interval"
                value={interval}
                onChange={(e) => setIntervalValue(e.target.value as OIExportInterval)}
                helperText={isMinuteDataset ? "Fixed for 1-minute option prices" : interval === "1minute" ? ONE_MINUTE_HELPER_TEXT : undefined}
                disabled={isMinuteDataset}
                fullWidth
              >
                {INTERVAL_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={6} sm={2}>
              <TextField
                select
                label="Strike Range"
                value={strikeMode}
                onChange={(e) => setStrikeMode(e.target.value as OIExportStrikeMode)}
                fullWidth
              >
                {isMinuteDataset ? (
                  <MenuItem value="ALL_STORED">All stored strikes</MenuItem>
                ) : (
                  <MenuItem value="ATM_BAND">ATM ± strikes</MenuItem>
                )}
                <MenuItem value="CUSTOM_RANGE">Custom range</MenuItem>
              </TextField>
            </Grid>
            {strikeMode === "ALL_STORED" ? null : strikeMode === "ATM_BAND" ? (
              <Grid item xs={6} sm={2}>
                <TextField
                  label="ATM ± strikes"
                  type="number"
                  value={atmBand}
                  onChange={(e) => setAtmBand(Math.max(1, Math.min(200, Number(e.target.value))))}
                  inputProps={{ min: 1, max: 200 }}
                  fullWidth
                />
              </Grid>
            ) : (
              <>
                <Grid item xs={6} sm={2}>
                  <TextField
                    label="Strike min"
                    type="number"
                    value={strikeMin}
                    onChange={(e) => setStrikeMin(e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField
                    label="Strike max"
                    type="number"
                    value={strikeMax}
                    onChange={(e) => setStrikeMax(e.target.value)}
                    fullWidth
                  />
                </Grid>
              </>
            )}
            <Grid item xs={6} sm={2}>
              <TextField
                select
                label="Export Format"
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value as OIExportFormat)}
                fullWidth
              >
                {EXPORT_FORMAT_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              {resolutionSummaryError && (
                <Alert severity="warning" sx={{ mb: 1.5 }}>{resolutionSummaryError}</Alert>
              )}
              {!resolutionSummaryError && filtersValid && (
                <Alert
                  severity={resolutionSummary?.is_mixed ? "warning" : "info"}
                  sx={{ mb: 1.5 }}
                >
                  {resolutionSummaryLoading || !resolutionSummary ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={14} />
                      <span>Checking actual data resolution…</span>
                    </Stack>
                  ) : (
                    <>
                      <Box>
                        Data quality — total: <strong>{resolutionSummary.total_records.toLocaleString()}</strong>
                        {", "}1-minute: <strong>{resolutionSummary.one_minute_records.toLocaleString()}</strong>
                        {", "}5-minute: <strong>{resolutionSummary.five_minute_records.toLocaleString()}</strong>
                        {resolutionSummary.other_records > 0 && (
                          <>
                            {", "}other: <strong>{resolutionSummary.other_records.toLocaleString()}</strong>
                          </>
                        )}
                        {resolutionSummary.unknown_records > 0 && (
                          <>
                            {", "}unknown: <strong>{resolutionSummary.unknown_records.toLocaleString()}</strong>
                          </>
                        )}
                        .
                      </Box>
                      {resolutionSummary.is_mixed && (
                        <Box sx={{ mt: 0.5, fontWeight: 600 }}>
                          {resolutionSummary.warning ??
                            "Historical data contains mixed time resolution. Export will include the actual stored records."}
                        </Box>
                      )}
                    </>
                  )}
                </Alert>
              )}
              {estimateError && <Alert severity="warning" sx={{ mb: 1.5 }}>{estimateError}</Alert>}
              {!estimateError && filtersValid && (
                <Alert severity={estimate?.exceeds_background_threshold ? "warning" : "info"} sx={{ mb: 1.5 }}>
                  {estimateLoading || !estimate ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={14} />
                      <span>Estimating record count…</span>
                    </Stack>
                  ) : (
                    <>
                      Estimated records: <strong>{estimate.estimated_record_count.toLocaleString()}</strong>
                      {estimate.is_estimate_approximate ? " (approximate)" : ""}.{" "}
                      {estimate.exceeds_background_threshold
                        ? "This exceeds the background-job threshold — it will run in the background and appear in the table below."
                        : "This will run immediately when you click Create Export."}
                    </>
                  )}
                </Alert>
              )}
              <Button
                variant="contained"
                onClick={handleCreateJob}
                disabled={creating || !filtersValid}
                startIcon={creating ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {creating ? "Creating export…" : "Create Export"}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {downloadError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDownloadError(null)}>
          {downloadError}
        </Alert>
      )}

      <Paper>
        <Box sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6">Export Jobs</Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Select
              size="small"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as OIExportJobStatus | "")}
              sx={{ minWidth: 160 }}
            >
              {JOB_STATUS_FILTER_OPTIONS.map((opt) => (
                <MenuItem key={opt.label} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            <Tooltip title="Refresh">
              <IconButton onClick={() => loadJobs()}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {jobsError && (
          <Alert severity="error" sx={{ mx: 2, mb: 2 }} onClose={() => setJobsError(null)}>
            {jobsError}
          </Alert>
        )}

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Export ID</TableCell>
                <TableCell>Requested By</TableCell>
                <TableCell>Symbol</TableCell>
                <TableCell>Date Range</TableCell>
                <TableCell>Record Count</TableCell>
                <TableCell>Status</TableCell>
                <TableCell sx={{ minWidth: 160 }}>Progress</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Completed</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((job) => (
                <TableRow key={job.id} hover>
                  <TableCell>
                    <Tooltip title={job.id}>
                      <span>{job.id.slice(0, 8)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{job.requested_by_email ?? "—"}</TableCell>
                  <TableCell>
                    {job.symbol_code}
                    {job.dataset === "OPTION_MINUTE_1MIN" && (
                      <Typography variant="caption" display="block" color="text.secondary">1-minute option prices</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {job.from_date} → {job.to_date}
                  </TableCell>
                  <TableCell>{formatRecordCount(job)}</TableCell>
                  <TableCell>
                    <ExportStatusChip status={job.status} />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ minWidth: 140 }}>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, job.progress_pct)}
                        sx={{ mb: 0.5 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {job.progress_pct.toFixed(0)}%
                      </Typography>
                      {job.status === "FAILED" && job.error_message && (
                        <Typography
                          variant="caption"
                          color="error.main"
                          display="block"
                          noWrap
                          title={job.error_message}
                        >
                          {job.error_message}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{formatDateTime(job.created_at)}</TableCell>
                  <TableCell>{formatDateTime(job.completed_at)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title={job.status === "COMPLETED" ? "Download" : "Available once completed"}>
                      <span>
                        <IconButton
                          disabled={job.status !== "COMPLETED" || downloadingId === job.id}
                          onClick={() => handleDownload(job)}
                        >
                          {downloadingId === job.id ? <CircularProgress size={20} /> : <DownloadIcon />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton color="error" onClick={() => setDeleteTarget(job)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {!jobsLoading && jobs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    No export jobs yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete export {deleteTarget?.id.slice(0, 8)}?</DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          This removes the generated file and cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={16} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default OIExportSection;
